/**
 * Iter 78 — Executive Notification Center
 *
 * Compact dashboard panel showing the top 5 highest-priority open /
 * acknowledged executive notifications, driven by
 * `/api/exec-notifications/top-priority` + `/api/exec-notifications/counts`.
 *
 * Placement: near the top of the Executive (Compliance) Dashboard, so
 * executives see urgent items immediately without scrolling.
 */
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ShieldAlert, ShieldCheck, Activity, ArrowUpRight,
  Bell, ExternalLink, Loader2, RefreshCw,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import "@/pages/executive/ExecutiveNotificationCenter.css";

const POSTURE_META = {
  normal:    { label: "Normal",                      tone: "ok",     icon: ShieldCheck },
  watch:     { label: "Monitor",                     tone: "watch",  icon: Activity },
  attention: { label: "Requires Attention",           tone: "warn",   icon: Bell },
  immediate: { label: "Immediate Action Required",    tone: "crit",   icon: ShieldAlert },
};

const SEVERITY_TONE = {
  critical: "crit",
  warning:  "warn",
  info:     "info",
};

function ageLabel(hours) {
  const h = Number(hours) || 0;
  if (h < 1) return `${Math.round(h * 60)}m`;
  if (h < 24) return `${Math.round(h)}h`;
  return `${Math.floor(h / 24)}d`;
}

export default function ExecutiveAttentionPanel({ variant = "default" }) {
  const nav = useNavigate();
  const [loading, setLoading] = useState(true);
  const [top, setTop] = useState([]);
  const [counts, setCounts] = useState(null);
  const [ackId, setAckId] = useState("");
  const [ackReason, setAckReason] = useState("");
  const [ackBusy, setAckBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [t, c] = await Promise.all([
        api.get("/exec-notifications/top-priority?limit=5"),
        api.get("/exec-notifications/counts"),
      ]);
      setTop(t.data.notifications || []);
      setCounts(c.data);
    } catch (e) {
      // Silent-fail on dashboard; console-report so we don't spam toast.
      console.warn("ExecutiveAttentionPanel load failed", e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const submitAck = async () => {
    if (ackReason.trim().length < 20) {
      toast.error("Acknowledge reason must be at least 20 characters");
      return;
    }
    setAckBusy(true);
    try {
      await api.post(`/exec-notifications/${ackId}/acknowledge`, { reason: ackReason.trim() });
      toast.success("Notification acknowledged");
      setAckId(""); setAckReason("");
      await load();
    } catch (e) {
      toast.error(formatApiError(e, "Could not acknowledge"));
    } finally {
      setAckBusy(false);
    }
  };

  const posture = POSTURE_META[counts?.posture] || POSTURE_META.normal;
  const Icon = posture.icon;

  return (
    <section className={`hw-eap hw-eap--${variant}`} data-testid="exec-attention-panel">
      <div className={`hw-eap__head hw-eap__head--${posture.tone}`}>
        <div className="hw-eap__title-block">
          <div className="hw-eap__eyebrow">Executive Notification Center</div>
          <h3 className="hw-eap__title" data-testid="exec-attention-title">
            <Icon className="w-4 h-4 hw-eap__title-icon" />
            Requires Executive Attention
          </h3>
        </div>
        <div className="hw-eap__meta">
          <div
            className={`hw-eap__posture hw-eap__posture--${posture.tone}`}
            data-testid="exec-attention-posture"
            data-posture={counts?.posture || ""}
          >
            {counts ? posture.label : "…"}
          </div>
          <div className="hw-eap__counts" data-testid="exec-attention-counts">
            <span data-testid="attention-count-critical" className="hw-eap__count hw-eap__count--crit">
              {counts?.critical_unread ?? 0} critical
            </span>
            <span data-testid="attention-count-warning" className="hw-eap__count hw-eap__count--warn">
              {counts?.warning_unread ?? 0} warning
            </span>
            <span data-testid="attention-count-escalated" className="hw-eap__count hw-eap__count--esc">
              {counts?.escalated_open ?? 0} escalated
            </span>
          </div>
          <div className="hw-eap__actions">
            <button
              type="button"
              className="hw-eap__ghost"
              onClick={load}
              disabled={loading}
              data-testid="exec-attention-refresh"
              title="Refresh"
            >
              {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
            </button>
            <button
              type="button"
              className="hw-eap__link"
              onClick={() => nav("/executive/notifications")}
              data-testid="exec-attention-open-center"
            >
              Open Notification Center <ArrowUpRight className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      </div>

      {loading && top.length === 0 ? (
        <div className="hw-eap__empty" data-testid="exec-attention-loading">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading executive signals…
        </div>
      ) : top.length === 0 ? (
        <div className="hw-eap__empty hw-eap__empty--calm" data-testid="exec-attention-empty">
          <ShieldCheck className="w-4 h-4" />
          <div>
            <strong>All quiet.</strong> No executive-priority notifications open right now.
          </div>
        </div>
      ) : (
        <ul className="hw-eap__list" data-testid="exec-attention-list">
          {top.map((n) => (
            <li key={n.id} className="hw-eap__item" data-testid={`exec-attention-item-${n.id}`}>
              <div className={`hw-eap__badge hw-eap__badge--${SEVERITY_TONE[n.severity] || "info"}`}>
                {n.severity}
              </div>
              <div className="hw-eap__content">
                <div className="hw-eap__row">
                  <span className="hw-eap__cat">{n.category}</span>
                  <span className="hw-eap__age">{ageLabel(n.age_hours)}</span>
                  {n.escalated && (
                    <span className="hw-eap__tag hw-eap__tag--esc" data-testid={`exec-attention-escalated-${n.id}`}>
                      Escalated
                    </span>
                  )}
                  {n.status === "acknowledged" && (
                    <span className="hw-eap__tag hw-eap__tag--ack">Acknowledged</span>
                  )}
                  <span className="hw-eap__prio" title="Priority score">
                    p{Math.round(n.priority || 0)}
                  </span>
                </div>
                <div className="hw-eap__msg" data-testid={`exec-attention-title-${n.id}`}>
                  {n.title}
                </div>
                <div className="hw-eap__sub">{n.body}</div>
              </div>
              <div className="hw-eap__cta">
                {n.deep_link && (
                  <button
                    type="button"
                    className="hw-eap__ghost"
                    onClick={() => nav(n.deep_link)}
                    data-testid={`exec-attention-deeplink-${n.id}`}
                    title="Deep link to related entity"
                  >
                    <ExternalLink className="w-3.5 h-3.5" />
                  </button>
                )}
                {n.status === "open" && (
                  <button
                    type="button"
                    className="hw-eap__primary"
                    onClick={() => { setAckId(n.id); setAckReason(""); }}
                    data-testid={`exec-attention-ack-${n.id}`}
                  >
                    Acknowledge
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      {ackId && (
        <div className="hw-eap__modal" data-testid="exec-attention-ack-modal" role="dialog" aria-modal="true">
          <div className="hw-eap__modal-card">
            <h4>Acknowledge notification</h4>
            <p className="hw-eap__modal-help">
              Explain the action taken or context (audit-logged, min 20 chars).
            </p>
            <textarea
              className="hw-eap__textarea"
              rows={3}
              maxLength={500}
              value={ackReason}
              onChange={(e) => setAckReason(e.target.value)}
              placeholder="e.g. Reviewed with security lead, tracking in incident #482…"
              data-testid="exec-attention-ack-reason"
            />
            <div className="hw-eap__modal-actions">
              <button
                type="button"
                className="hw-eap__ghost"
                onClick={() => { setAckId(""); setAckReason(""); }}
                data-testid="exec-attention-ack-cancel"
                disabled={ackBusy}
              >
                Cancel
              </button>
              <button
                type="button"
                className="hw-eap__primary"
                onClick={submitAck}
                disabled={ackBusy || ackReason.trim().length < 20}
                data-testid="exec-attention-ack-submit"
              >
                {ackBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
                Confirm acknowledge
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
